import { GroupsDemoEmptyPage } from "@components/common/empty-page/groups-demo-empty-page";
import { GroupsEmptyPage } from "@components/common/empty-page/groups-empty-page";
import { PageContainer } from "@components/common/page-container";
import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";
import { GroupsSortBy } from "@custom-types/group-types";
import { isGroupsSortBy } from "@custom-types/type-guards";
import { useGroups } from "@hooks/use-groups";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { GroupsActionButtons } from "@pages/groups/groups-action-buttons";
import { NotFoundPage } from "@pages/not-found-page";
import { useAppParams } from "@router/router-helper";
import {
  fetchingGroupsFlagsSelector,
  filteredCompanyGroupsSelector,
} from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { isGroupDemo, sortGroups } from "@utils/group-utils";
import { useMemo } from "react";
import { GroupsPageTable } from "src/pages/groups/groups-page-table";
import { GroupsTiles } from "src/pages/groups/groups-tiles";

/** Content in the Groups page */
export function GroupsPage(): JSX.Element {
  const { companyId } = useAppParams();
  const groups = useGroups();
  const filteredCompanyGroups = useAppSelector(filteredCompanyGroupsSelector);
  const { groups: viewMode } = useAppSelector(dataViewModeSelector);
  const { isFetchingGroups } = useAppSelector(fetchingGroupsFlagsSelector);
  const { sortBy, setSortBy } = useSubjectsSortByQuery<GroupsSortBy>({
    defaultSort: DEFAULT_GROUPS_SORT_BY,
    isValidSort: isGroupsSortBy,
  });
  
  // Sort the filtered groups client-side
  const sortedGroups = useMemo(
    () => sortGroups(filteredCompanyGroups, sortBy),
    [filteredCompanyGroups, sortBy]
  );
  if (!companyId) {
    return <NotFoundPage />;
  }

  if (
    groups.length === 1 &&
    isGroupDemo(groups[0]) &&
    filteredCompanyGroups.length === 1
  ) {
    return (
      <GroupsDemoEmptyPage
        companyId={companyId}
        demoGroup={filteredCompanyGroups[0]}
      />
    );
  }

  const shouldShowEmptyPage =
    filteredCompanyGroups.length === 0 && !isFetchingGroups;

  let groupsContent = (
    <GroupsPageTable
      companyId={companyId}
      groups={sortedGroups}
      isLoading={isFetchingGroups}
      sortBy={sortBy}
      setSortBy={setSortBy}
    />
  );

  if (viewMode === "cards") {
    groupsContent = (
      <GroupsTiles groups={sortedGroups} sortBy={sortBy} setSortBy={setSortBy} />
    );
  }

  return (
    <PageContainer
      title="Groups Management"
      dataTestId="GROUPS_CONTAINER"
      actionButtons={<GroupsActionButtons groups={groups} />}
    >
      {/* If empty page exist, show it. If not show the content */}
      {shouldShowEmptyPage ? <GroupsEmptyPage /> : groupsContent}
    </PageContainer>
  );
}
